import { Provider } from '@angular/core';
import { CurrencyDomainService, CurrencyRateDomainService } from '@app/domain/services';
import {
  GetCurrenciesUseCase,
  GetAvailableCurrenciesUseCase,
  GetCurrencyRateUseCase,
  CreateCurrencyUseCase,
  UpdateCurrencyUseCase,
  DeleteCurrencyUseCase,
  ReorderCurrenciesUseCase,
} from '@app/domain/use-cases';
import type { ICurrencyRepository, ICurrencyRateRepository, ICurrencyRateProvider } from '@app/domain/ports';
import { CURRENCY_REPOSITORY, RATE_REPOSITORY } from '../../tokens/infrastructure.tokens';
import { CURRENCY_RATE_PROVIDER } from '@app/ui/stores';

/**
 * Provides Currency domain services and use cases.
 *
 * Includes:
 * - CurrencyDomainService: Business rules and validation
 * - 7 Use Cases: Get, GetAvailable, GetRate, Create, Update, Delete, Reorder
 *
 * All providers use constructor injection pattern following Clean Architecture.
 */
export function provideCurrencyDomain(): Provider[] {
  return [
    // Domain Service
    {
      provide: CurrencyDomainService,
      useFactory: (currencyRepository: ICurrencyRepository) => new CurrencyDomainService(currencyRepository),
      deps: [CURRENCY_REPOSITORY],
    },

    // Use Cases - Query
    {
      provide: GetCurrenciesUseCase,
      useFactory: (currencyRepository: ICurrencyRepository) => new GetCurrenciesUseCase(currencyRepository),
      deps: [CURRENCY_REPOSITORY],
    },
    {
      provide: GetAvailableCurrenciesUseCase,
      useFactory: (currencyRepository: ICurrencyRepository) => new GetAvailableCurrenciesUseCase(currencyRepository),
      deps: [CURRENCY_REPOSITORY],
    },
    {
      provide: GetCurrencyRateUseCase,
      useFactory: (
        currencyRepository: ICurrencyRepository,
        CurrencyRateRepository: ICurrencyRateRepository,
        currencyRateProvider: ICurrencyRateProvider,
        domainService: CurrencyRateDomainService,
      ) => new GetCurrencyRateUseCase(currencyRepository, CurrencyRateRepository, currencyRateProvider, domainService),
      deps: [CURRENCY_REPOSITORY, RATE_REPOSITORY, CURRENCY_RATE_PROVIDER, CurrencyRateDomainService],
    },

    // Use Cases - Commands
    {
      provide: CreateCurrencyUseCase,
      useFactory: (currencyRepository: ICurrencyRepository, currencyDomainService: CurrencyDomainService) =>
        new CreateCurrencyUseCase(currencyRepository, currencyDomainService),
      deps: [CURRENCY_REPOSITORY, CurrencyDomainService],
    },
    {
      provide: UpdateCurrencyUseCase,
      useFactory: (currencyRepository: ICurrencyRepository, currencyDomainService: CurrencyDomainService) =>
        new UpdateCurrencyUseCase(currencyRepository, currencyDomainService),
      deps: [CURRENCY_REPOSITORY, CurrencyDomainService],
    },
    {
      provide: DeleteCurrencyUseCase,
      useFactory: (currencyRepository: ICurrencyRepository, currencyDomainService: CurrencyDomainService) =>
        new DeleteCurrencyUseCase(currencyRepository, currencyDomainService),
      deps: [CURRENCY_REPOSITORY, CurrencyDomainService],
    },
    {
      provide: ReorderCurrenciesUseCase,
      useFactory: (currencyRepository: ICurrencyRepository) =>
        new ReorderCurrenciesUseCase(currencyRepository),
      deps: [CURRENCY_REPOSITORY],
    },
  ];
}
